import { useEffect, useState } from "react";
import RightModal from "../components/templates/RightModal";
import { getUsersList, selectUsersData, selectUsersLoading } from "../redux/users/usersSlice";
import DynamicIcon from "../components/DynamicIcon";
import LoadingSection from "../components/LoadingSection";
import { selectAccountData } from "../redux/main/accountSlice";
import { resetRefreshNow, selectRightModalId, selectRightModalIsOpen, selectRightModalIsRefreshNow, selectRightModalmode, setModal } from "../redux/rightModalSlice";
import { useDispatch, useSelector } from "react-redux";
import AddEditUser from "../components/usersPage/AddEditUser";
import UserGroupsModal from "../components/usersPage/UserGroupsModal";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useColorPalette } from '../hooks/useColorPalette';
import DataTable from "../components/DataTable";

export default function Users() {
    const dispatch = useDispatch();
    const palette = useColorPalette();
    const usersList = useSelector(selectUsersData);
    const usersLoading = useSelector(selectUsersLoading);
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');
    const [dataTableKey, setDataTableKey] = useState(Date.now());
    const [searchTerm, setSearchTerm] = useState('');
    
    // Stato per la modale di conferma eliminazione
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    // Stato per la modale dei gruppi utenti
    const [showGroupsModal, setShowGroupsModal] = useState(false);
    // RightModal gestita tramite Redux
    const rightModalIsOpen = useSelector(selectRightModalIsOpen);
    const rightModalMode = useSelector(selectRightModalmode);
    const rightModalUserId = useSelector(selectRightModalId);
    
    // Helper function per verificare se un campo è criptato
    const isEncrypted = (value) => {
        if (!value) return false;
        // Formato criptato: salt:hash (es: 30363335313038373838393237373832:7af271...)
        return /^[0-9a-f]+:[0-9a-f]+$/i.test(value);
    };
    
    // Helper function per mostrare valori sicuri
    const displayValue = (value, placeholder = '-') => {
        if (!value) return placeholder;
        if (isEncrypted(value)) {
            return '[Dato criptato - contattare assistenza]';
        }
        return value;
    };
    
    // Gestione della paginazione con DataTable
    const handlePageChange = (pageIndex, pageSize) => {
        console.log('Cambio pagina a:', pageIndex, 'dimensione:', pageSize);
        // Calcolo l'offset in base all'indice della pagina e alla dimensione
        const start = pageIndex * pageSize;
        
        dispatch(getUsersList({
            accountId: accountData?._id,
            token,
            search: searchTerm.length >= 3 ? searchTerm : '',
            start,
            length: pageSize
        }));
        dispatch(resetRefreshNow());
    };
    
    // Gestione della ricerca
    const handleSearch = (searchValue) => {
        console.log('Ricerca:', searchValue);
        setSearchTerm(searchValue);
        
        if (searchValue.length >= 3 || searchValue.length === 0) {
            dispatch(getUsersList({
                accountId: accountData?._id,
                token,
                search: searchValue.length >= 3 ? searchValue : '',
                start: 0,
                length: 50
            }));
            dispatch(resetRefreshNow());
        }
    };
    
    // Eseguo la chiamata iniziale quando il componente viene montato
    useEffect(() => {
        if (accountData?._id) {
            dispatch(getUsersList({
                accountId: accountData._id,
                token,
                start: 0,
                length: 50
            }));
            dispatch(resetRefreshNow());
        }
    }, [dispatch, accountData, token]);
    
    // Avvio il refresh dei dati quando isRefreshNow della RightModal si attiva
    const rightModalIsRefreshNow = useSelector(selectRightModalIsRefreshNow);
    useEffect(() => {
        if (rightModalIsRefreshNow) {
            dispatch(getUsersList({
                accountId: accountData?._id,
                token,
                start: 0,
                length: 50
            }));
            dispatch(resetRefreshNow());
            setDataTableKey(Date.now()); // Aggiorniamo la chiave per forzare il re-render della tabella
        }
    }, [rightModalIsRefreshNow]);
    
    // Aggiorna la tabella quando il modal viene chiuso
    useEffect(() => {
        if (!rightModalIsOpen && dataTableKey) {
            // Aggiorna la tabella
            setDataTableKey(Date.now());
        }
    }, [rightModalIsOpen]);
    

    
    // Funzione per mostrare la modale di conferma eliminazione
    const handleDeleteClick = (user) => {
        setUserToDelete(user);
        setShowDeleteModal(true);
    };
    
    // Funzione per abilitare/disabilitare un utente
    const handleToggleActive = async (user) => {
        if (!user || !accountData?._id) return;
        
        try {
            const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/users`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                },
                body: JSON.stringify({
                    _id: user._id,
                    active: !user.active,
                    accountId: accountData._id
                })
            });
            
            if (response.ok) {
                // Aggiorna la lista degli utenti
                dispatch(getUsersList({
                    accountId: accountData._id,
                    token
                }));
                
                toast.success(
                    user.active ? 'Utente disabilitato' : 'Utente abilitato', 
                    { position: "top-right", autoClose: 2000 }
                );
            } else {
                const errorData = await response.json();
                toast.error('Errore durante l\'operazione: ' + (errorData.message || 'Errore sconosciuto'));
            }
        } catch (error) {
            console.error('Errore:', error);
            toast.error('Errore di connessione');
        }
    };
    
    // Funzione per confermare l'eliminazione dell'utente
    const confirmDelete = async () => {
        if (!userToDelete || !accountData?._id) return;
        
        try {
            // Chiamata all'API DELETE /users
            const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/users`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                },
                body: JSON.stringify({
                    userId: userToDelete._id,
                    accountId: accountData._id
                })
            });
            
            if (response.ok) {
                // Aggiorna la lista degli utenti
                dispatch(getUsersList({
                    accountId: accountData._id,
                    token
                }));
                
                // Mostra un messaggio di successo con toast
                toast.success('Utente eliminato con successo', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            } else {
                const errorData = await response.json();
                console.error('Errore durante l\'eliminazione dell\'utente:', errorData);
                toast.error(`Errore durante l'eliminazione dell'utente: ${errorData.message || 'Si è verificato un errore'}`, {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });  
            }
        } catch (error) {
            console.error('Errore durante l\'eliminazione dell\'utente:', error);
            toast.error(`Errore durante l'eliminazione dell'utente: ${error.message || 'Si è verificato un errore'}`, {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });  
        } finally {
            // Chiudi la modale in ogni caso
            setShowDeleteModal(false);
            setUserToDelete(null);
        }
    };
    
    // Funzione per annullare l'eliminazione
    const cancelDelete = () => {
        setShowDeleteModal(false);
        setUserToDelete(null);
    };
    
    return (
        <div className="page">
            {usersLoading && <LoadingSection isFixed />}
            <h1><span className="font-light">Utenti / </span>Lista Utenti</h1>
            <div className="box">

                {/* Pulsanti azioni */}
                <div className="flex gap-2">
                    <button className="primary" onClick={() => dispatch(setModal({ mode: 'add' }))}><DynamicIcon iconClass="fa-solid fa-plus" />Nuovo Utente</button>
                    <button 
                        className="secondary" 
                        onClick={() => setShowGroupsModal(true)}
                        style={{ backgroundColor: palette.primary, borderColor: palette.primary, color: 'white' }}
                    >
                        <DynamicIcon iconClass="fa-solid fa-users" style={{ color: 'white' }} />
                        Gestione Gruppi
                    </button>
                </div>

                {/* DataTable per gli utenti */}
                <div className="w-full my-4 overflow-x-auto">
                    <DataTable
                        key={dataTableKey}
                        columns={[
                            { 
                                id: 'color', 
                                title: 'COLORE',
                                width: '8%',
                                render: (user) => (
                                    <div className="flex items-center justify-center">
                                        <div
                                            className="w-6 h-6 rounded-full border border-gray-300"
                                            style={{
                                                backgroundColor: user.color || '#CCCCCC',
                                            }}
                                        ></div>
                                    </div>
                                )
                            },
                            { 
                                id: 'name', 
                                title: 'NOME',
                                width: '12%',
                                render: (user) => (
                                    <div className="text-left uppercase">
                                        {user.name || '-'}
                                    </div>
                                )
                            },
                            { 
                                id: 'surname', 
                                title: 'COGNOME',
                                width: '12%',
                                render: (user) => (
                                    <div className="text-left uppercase">
                                        {user.surname || '-'}
                                    </div>
                                )
                            },
                            { 
                                id: 'email', 
                                title: 'EMAIL',
                                width: '20%',
                                render: (user) => (
                                    <div className="text-left" style={{ 
                                        color: isEncrypted(user.email) ? '#ef4444' : 'inherit',
                                        fontSize: isEncrypted(user.email) ? '0.75rem' : 'inherit'
                                    }}>
                                        {displayValue(user.email)}
                                    </div>
                                )
                            },
                            { 
                                id: 'role', 
                                title: 'RUOLO',
                                width: '12%',
                                render: (user) => (
                                    <div className="text-left uppercase">
                                        {user.roleLabel || '-'}
                                    </div>
                                )
                            },
                            { 
                                id: 'type', 
                                title: 'TIPO',
                                width: '12%',
                                render: (user) => (
                                    <div className="text-left uppercase">
                                        {user.typeLabel || '-'}
                                    </div>
                                )
                            },
                            { 
                                id: 'active', 
                                title: 'ATTIVO',
                                width: '8%',
                                render: (user) => (
                                    <div className="text-center">
                                        {user.active ? (
                                            <DynamicIcon iconClass="fa-solid fa-check-circle" style={{ color: '#10b981', fontSize: '1.2rem' }} />
                                        ) : (
                                            <DynamicIcon iconClass="fa-solid fa-times-circle" style={{ color: '#ef4444', fontSize: '1.2rem' }} />
                                        )}
                                    </div>
                                )
                            },
                            { 
                                id: 'actions', 
                                title: 'AZIONI',
                                width: '16%',
                                render: (user) => (
                                    <div className="flex gap-3 justify-center">
                                        <button 
                                            style={{ color: 'inherit' }}
                                            title="Modifica utente"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                dispatch(setModal({
                                                    isOpen: true,
                                                    mode: 'edit',
                                                    id: user._id
                                                }));
                                            }}
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-edit" />
                                        </button>
                                        <button 
                                            style={{ color: user.active ? '#ef4444' : '#10b981' }}
                                            title={user.active ? 'Disabilita utente' : 'Abilita utente'}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleToggleActive(user);
                                            }}
                                        >
                                            <DynamicIcon iconClass={user.active ? "fa-solid fa-user-slash" : "fa-solid fa-user-check"} />
                                        </button>
                                        <button 
                                            style={{ color: 'inherit' }}
                                            title="Elimina utente"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteClick({
                                                    _id: user._id,
                                                    name: user.name,
                                                    surname: user.surname
                                                });
                                            }}
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-trash" />
                                        </button>
                                    </div>
                                )
                            }
                        ]}
                        data={usersList?.data || []}
                        loading={usersLoading}
                        totalRecords={usersList?.recordsFiltered || 0}
                        pageSize={50}
                        onPageChange={handlePageChange}
                        hasSearch={true}
                        onSearch={handleSearch}
                        initialSearchTerm={searchTerm}
                        emptyMessage="Nessun utente trovato"
                        renderCustomRow={(user, index, isEven, tableStyles) => {
                            // Funzione per gestire il click sulla riga
                            const handleRowClick = () => {
                                dispatch(setModal({
                                    isOpen: true,
                                    mode: 'view',
                                    id: user._id
                                }));
                            };
                            
                            return (
                                <div 
                                    key={user._id}
                                    className="data-table-row cursor-pointer"
                                    style={{
                                        backgroundColor: isEven ? tableStyles.evenRow.backgroundColor : tableStyles.oddRow.backgroundColor,
                                        color: isEven ? tableStyles.evenRow.color : tableStyles.oddRow.color,
                                    }}
                                    onClick={handleRowClick}
                                >
                                    {/* Colonna COLORE */}
                                    <div className="data-table-cell" style={{ width: '8%' }} data-label="COLORE">
                                        <div className="flex items-center justify-center">
                                            <div
                                                className="w-6 h-6 rounded-full border border-gray-300"
                                                style={{
                                                    backgroundColor: user.color || '#CCCCCC',
                                                }}
                                            ></div>
                                        </div>
                                    </div>
                                    
                                    {/* Colonna NOME */}
                                    <div className="data-table-cell" style={{ width: '12%' }} data-label="NOME">
                                        <div className="text-left uppercase">
                                            {user.name || '-'}
                                        </div>
                                    </div>
                                    
                                    {/* Colonna COGNOME */}
                                    <div className="data-table-cell" style={{ width: '12%' }} data-label="COGNOME">
                                        <div className="text-left uppercase">
                                            {user.surname || '-'}
                                        </div>
                                    </div>
                                    
                                    {/* Colonna EMAIL */}
                                    <div className="data-table-cell" style={{ width: '20%' }} data-label="EMAIL">
                                        <div className="text-left" style={{ 
                                            color: isEncrypted(user.email) ? '#ef4444' : 'inherit',
                                            fontSize: isEncrypted(user.email) ? '0.75rem' : 'inherit'
                                        }}>
                                            {displayValue(user.email)}
                                        </div>
                                    </div>
                                    
                                    {/* Colonna RUOLO */}
                                    <div className="data-table-cell" style={{ width: '12%' }} data-label="RUOLO">
                                        <div className="text-left uppercase">
                                            {user.roleLabel || '-'}
                                        </div>
                                    </div>
                                    
                                    {/* Colonna TIPO */}
                                    <div className="data-table-cell" style={{ width: '12%' }} data-label="TIPO">
                                        <div className="text-left uppercase">
                                            {user.typeLabel || '-'}
                                        </div>
                                    </div>
                                    
                                    {/* Colonna ATTIVO */}
                                    <div className="data-table-cell" style={{ width: '8%' }} data-label="ATTIVO">
                                        <div className="text-center">
                                            {user.active ? (
                                                <DynamicIcon iconClass="fa-solid fa-check-circle" style={{ color: '#10b981', fontSize: '1.2rem' }} />
                                            ) : (
                                                <DynamicIcon iconClass="fa-solid fa-times-circle" style={{ color: '#ef4444', fontSize: '1.2rem' }} />
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Colonna AZIONI */}
                                    <div className="data-table-cell" style={{ width: '16%' }} data-label="AZIONI">
                                        <div className="flex gap-3 justify-center">
                                            <button 
                                                style={{ color: isEven ? tableStyles.evenRow.color : tableStyles.oddRow.color }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    dispatch(setModal({ 
                                                        isOpen: true,
                                                        mode: 'edit',
                                                        id: user._id
                                                    }));
                                                }}
                                            >
                                                <DynamicIcon iconClass="fa-solid fa-edit" />
                                            </button>
                                            <button 
                                                style={{ color: isEven ? tableStyles.evenRow.color : tableStyles.oddRow.color }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteClick({
                                                        _id: user._id,
                                                        name: user.name,
                                                        surname: user.surname
                                                    });
                                                }}
                                            >
                                                <DynamicIcon iconClass="fa-solid fa-trash" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        }}
                    />
                </div>
            </div>

            {/* RIGHT MODAL per inserimento o modifica utente */}
            {
                rightModalIsOpen && <RightModal>
                    {(rightModalMode === 'add' || rightModalMode === 'edit') && <AddEditUser userId={rightModalUserId} />}
                    {
                        rightModalMode === 'view' && rightModalUserId &&
                        <div className="w-full p-8">
                            <h1>Dettagli Utente</h1>
                            <div className="light-text mt-[-24px]">Id utente: {rightModalUserId}</div>
                        </div>
                    }
                </RightModal>
            }
            
            {/* Modale di conferma eliminazione */}
            {showDeleteModal && userToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4 transform transition-all">
                        <div className="text-center mb-4">
                            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-3">
                                <i className="fa-solid fa-trash text-red-600 text-2xl"></i>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">Elimina utente</h3>
                            <p className="text-gray-500">
                                Sei sicuro di voler eliminare l'utente <strong>{userToDelete.name} {userToDelete.surname}</strong>? 
                                Questa azione non può essere annullata.
                            </p>
                        </div>
                        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 mt-6">
                            <button
                                onClick={cancelDelete}
                                type="button"
                                className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all"
                            >
                                Annulla
                            </button>
                            <button
                                onClick={confirmDelete}
                                type="button"
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all"
                            >
                                Elimina
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Modale gestione gruppi utenti */}
            <UserGroupsModal 
                isOpen={showGroupsModal} 
                toggle={() => setShowGroupsModal(false)} 
            />
        </div>
    );
}
